import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AccountMember } from '@celum/authentication';
import { AVATAR_SIZE, AvatarConfigBuilder, AvatarConfiguration, CelumAvatar } from '@celum/common-components/avatar';
import { AccountUser } from '@celum/shared/domain';
import { accountUserOrMemberToAvatarBuilder } from '@celum/shared/util';

export type AvatarConfigAdapterFn = (member: AccountMember, avatarConfig: AvatarConfigBuilder<AvatarConfiguration>) => AvatarConfigBuilder<AvatarConfiguration>;

@Component({
  selector: 'celum-user-avatar',
  imports: [MatTooltipModule, CelumAvatar],
  templateUrl: './user-avatar.component.html',
  styleUrls: ['./user-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserAvatarComponent {
  public user = input.required<Partial<AccountUser | AccountMember>>();
  public avatarConfigAdapterFn = input<AvatarConfigAdapterFn>();
  /** @deprecated Will be removed once all applications switch to cookie-based authentication for avatars */
  public accountAccessToken = input<string>();
  public managerBadgeTooltip = input('');
  public managers = input<string[]>([]);
  public invitedTooltip = input<string>();
  public rejectedTooltip = input<string>();
  public deactivatedTooltip = input<string>();
  public size = input<AVATAR_SIZE | number>(AVATAR_SIZE.m);
  public fontSize = input<number>();
  public showTooltip = input<boolean>(true);
  /** Define a custom tooltip text. This will take effect only for active users. For other states, the tooltip is not changeable. */
  public tooltipText = input<string>();
  /** Whether to show the user's display name next to the avatar. */
  public showDisplayName = input<boolean>(false);

  private isManager = computed(() => {
    const user = this.user();
    const managers = this.managers() ?? [];
    return !!user && (managers.includes(user.id) || managers.includes((user as AccountMember).oid));
  });

  protected config = computed(() => this.getAvatarConfiguration());

  private getAvatarConfiguration(): AvatarConfiguration {
    let avatarConfigurationBuilder = accountUserOrMemberToAvatarBuilder(this.user() as AccountUser | AccountMember, {
      tooltipText: this.tooltipText(),
      size: this.size(),
      fontSize: this.fontSize(),
      showTooltip: this.showTooltip(),
      showDisplayName: this.showDisplayName(),
      accountAccessToken: this.accountAccessToken(),
      deactivatedTooltip: this.deactivatedTooltip(),
      invitedTooltip: this.invitedTooltip(),
      rejectedTooltip: this.rejectedTooltip(),
      managerBadgeTooltip: this.managerBadgeTooltip(),
      isManager: this.isManager()
    });

    avatarConfigurationBuilder = this.avatarConfigAdapterFn()?.(this.user() as AccountMember, avatarConfigurationBuilder) ?? avatarConfigurationBuilder;

    return avatarConfigurationBuilder.build();
  }
}
